<template>
  <div>
    <label :id="text.toLowerCase().replaceAll(' ', '-')" class="twcs-block twcs-text-base twcs-font-medium twcs-text-kctcs-navy-800">
      <template v-if="options">
        {{ text }}
      </template>
      <template v-else>
        Loading...
      </template>
    </label>
    <div class="twcs-mt-1 twcs-relative">
      <button
        type="button"
        role="combobox"
        aria-haspopup="listbox"
        :aria-expanded="isOpen"
        :aria-label="multiselect ? text : false"
        :aria-labelledby="!multiselect ? text.toLowerCase().replaceAll(' ', '-') : false"
        :disabled="options.length === 0 || loading"
        ref="button"
        @click="onButtonClick"
        @keydown.arrow-up.stop.prevent="onButtonClick"
        @keydown.arrow-down.stop.prevent="onButtonClick"
        :class="[loading ? 'twcs-bg-gray-300': 'twcs-bg-white', 'twcs-relative twcs-w-48 twcs-border twcs-border-gray-300 twcs-pl-3 twcs-pr-10 twcs-py-2 twcs-text-left twcs-cursor-default focus-visible:twcs-outline-none focus-visible:twcs-ring-1 focus-visible:twcs-ring-kctcs-navy-500 focus-visible:twcs-border-kctcs-navy-500 sm:twcs-text-base']"
      >
        <span v-if="loading" class="twcs-block twcs-truncate">
          Loading...
        </span>
        <span v-else class="twcs-block twcs-truncate">
          {{ comboboxText }}
        </span>
        <span class="twcs-absolute twcs-inset-y-0 twcs-right-0 twcs-flex twcs-items-center twcs-pr-2 twcs-pointer-events-none">
          <!-- Heroicon name: selector -->
          <svg class="twcs-h-5 twcs-w-5 twcs-text-gray-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
            <path fill-rule="evenodd" d="M10 3a1 1 0 01.707.293l3 3a1 1 0 01-1.414 1.414L10 5.414 7.707 7.707a1 1 0 01-1.414-1.414l3-3A1 1 0 0110 3zm-3.707 9.293a1 1 0 011.414 0L10 14.586l2.293-2.293a1 1 0 011.414 1.414l-3 3a1 1 0 01-1.414 0l-3-3a1 1 0 010-1.414z" clip-rule="evenodd" />
          </svg>
        </span>
      </button>
      <Transition
        leave-active-class="twcs-transition twcs-ease-in twcs-duration-100"
        leave-from-class="twcs-opacity-100"
        leave-to-class="twcs-opacity-0"
      >
        <!--
          Select popover, show/hide based on select state.

          Entering: ""
            From: ""
            To: ""
          Leaving: "transition ease-in duration-100"
            From: "opacity-100"
            To: "opacity-0"
        -->
        <div
          v-show="isOpen"
          v-click-outside="onClickAway"
          class="twcs-absolute twcs-right-0 twcs-left-0 twcs-mt-1 twcs-w-min twcs-min-w-full twcs-bg-white twcs-shadow-lg twcs-z-[1000]"
        >
          <div
            ref="listbox"
            role="listbox"
            tabindex="0"
            :aria-labelledby="text.toLowerCase().replaceAll(' ', '-')"
            :aria-activedescendant="activeDescendant"
            :aria-multiselectable="multiselect"
            @keydown.enter.stop.prevent="onOptionSelect(selectedCategory, selectedIndex)"
            @keydown.space.stop.prevent="onOptionSelect(selectedCategory, selectedIndex)"
            @keydown.arrow-up.prevent="onArrowUp()"
            @keydown.arrow-down.prevent="onArrowDown()"
            @keydown.escape="onEscape()"
            class="twcs-max-h-60 twcs-text-base twcs-ring-1 twcs-ring-black twcs-ring-opacity-5 twcs-overflow-y-auto twcs-w-full focus-visible:twcs-outline-none sm:twcs-text-base"
          >
            <div
              v-for="(category, categoryIndex) in Object.keys(options)"
              :key="category"
              @mouseenter="onMouseMoveCategory(category, categoryIndex)"
              @mouseleave="onMouseMoveCategory(null, null)"
              :id="`${source}-${categoryIndex}`"
              class="twcs-relative"
            >
              <div class="twcs-z-[1001] twcs-sticky twcs-top-0 twcs-border-t twcs-border-b twcs-border-gray-200 twcs-bg-gray-50 twcs-px-6 twcs-py-1 twcs-text-base twcs-font-medium twcs-text-gray-500" role="presentation">
                <span v-if="category === '0'" class="twcs-block twcs-truncate twcs-w-full">{{ catchAllCategory }}</span>
                <span v-else class="twcs-block twcs-truncate twcs-w-full">{{ category }}</span>
              </div>
              <ul class="twcs-relative twcs-z-[1000] twcs-divide-y twcs-divide-gray-200" role="group" :aria-label="category === '0' ? catchAllCategory : category">
                <!--
                  Select option, manage highlight styles based on mouseenter/mouseleave and keyboard navigation.

                  Highlighted: "text-white bg-kctcs-navy-600", Not Highlighted: "text-gray-900"
                -->
                <li
                  v-for="(option, index) in options[category]"
                  :key="option.text"
                  role="option"
                  :id="`${source}-${categoryIndex}-item-${index}`"
                  :aria-selected="values[category][index]"
                  @click="onOptionSelect(category, index)"
                  @mouseenter="onMouseMoveOption(index)"
                  @mouseleave="onMouseMoveOption(null)"
                  :class="[
                    selectedIndex === index && selectedCategory === category
                    ? 'twcs-text-white twcs-bg-kctcs-navy-600'
                    : 'twcs-text-gray-900',
                    'twcs-cursor-default twcs-select-none twcs-relative twcs-w-full twcs-py-2 twcs-pl-8 twcs-pr-4'
                  ]"
                >
                  <!-- Selected: "font-semibold", Not Selected: "font-normal" -->
                  <span
                    :class="[
                      values[category][index]
                      ? 'twcs-font-semibold'
                      : 'twcs-font-normal',
                      'twcs-block twcs-truncate twcs-w-full'
                    ]"
                  >
                    {{ option.text }}
                  </span>

                  <!--
                    Checkmark, only display for selected option.

                    Highlighted: "text-white", Not Highlighted: "text-kctcs-navy-600"
                  -->
                  <span
                    v-if="values[category][index]"
                    aria-hidden="true"
                    :class="[
                      selectedIndex === index && selectedCategory === category
                      ? 'twcs-text-white'
                      : 'twcs-text-kctcs-navy-600',
                      'twcs-absolute twcs-inset-y-0 twcs-left-0 twcs-flex twcs-items-center twcs-pl-1.5'
                    ]"
                  >
                    <!-- Heroicon name: check -->
                    <svg class="twcs-h-5 twcs-w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
                    </svg>
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, nextTick, onMounted } from 'vue';
import Bugsnag from '@bugsnag/js';

let props = defineProps({
	// text to show/key in options object in parent component (when lowercased)
	text: {
		type: String,
		required: true,
	},
	// if lowercase of text is not the 'source', is set here
	source: {
		type: String,
		required: true,
	},
	catchAllCategory: {
		type: String,
		default: 'All',
	},
	// array of options
	options: {
		type: Object,
		default: () => {},
	},
	//
	loading: {
		type: Boolean,
		default: true,
	},
	// array of booleans that represent if the corresponding option
	// (at same index in options array) is selected
	values: {
		type: Object,
		default: () => {},
	},
	// determines if dropdown is multiselect
	multiselect: {
		type: Boolean,
		default: false,
	},
});

let emit = defineEmits([
	'filterChanged',
	'filtersFinishedChanging',
]);

let isOpen = ref(false);
let selectedCategory = ref('0');
let selectedCategoryIndex = ref(0);
let selectedIndex = ref(0); // hover/listbox-focused element
let activeDescendant = ref(''); // active descendant of ul element (id of option element)
let listbox = ref(null); // ul element
let button = ref(null); // button element
let isMounted = ref(false);

let comboboxText = computed(() => {
    if (props.multiselect) {
        let optionsText = [];
        for (const optionKey in props.options) {
            let options = props.options[optionKey].filter((option, index) => props.values[optionKey][index]).map((option) => option.text);
            optionsText = optionsText.concat(options);
        }
        if (optionsText.length === 1) {
            return optionsText[0];
        } else if (optionsText.length === 2) {
            return optionsText[0] + ' and ' + optionsText[1];
        } else {
            return optionsText.reduce((carry, current, index, array) => index === 0 ? current :  index === array.length - 1 ? carry + ', and ' + current : carry + ', ' + current, '');
        }
    } else {
        for (const optionKey in props.options) {
            let valueIndex = props.values[optionKey].findIndex((value) => value);
            if (valueIndex !== -1) {
                return props.options[optionKey][valueIndex].text;
            }
        }
    }
});

onMounted(() => {
    isMounted.value = true;
});

function onClickAway(event, el) {
	isOpen.value = false;
}

function setActiveDescendant() {
    activeDescendant.value = `${props.source}-${selectedCategoryIndex.value}-item-${selectedIndex.value}`;
}

function onButtonClick(event, el) {
	if(isOpen.value) {
		isOpen.value = false;
		return;
	}
	isOpen.value = true;
	nextTick(() => {
		listbox.value.focus();
		if(selectedCategory.value === null || selectedCategoryIndex.value === null || selectedIndex.value === null) {
			selectedCategory.value = '0';
			selectedCategoryIndex.value = 0;
			selectedIndex.value = 0;
		}
        setActiveDescendant(selectedCategoryIndex.value, selectedIndex.value);
        if (isMounted) {
		    listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
        }
	});
}

function choose(category, index) {
	if(!props.multiselect) { // case: not multiselect
		// if one if selected all others must be false
		Object.keys(props.values).forEach((key) => {
			props.values[key].forEach((value, idx) => {
				if(value) {
					emit('filterChanged', {
						source: props.source,
						text: props.text,
						key,
						index: idx,
						value: false,
					});
				}
			});
		});
		emit('filterChanged', {
			source: props.source,
			text: props.text,
			index,
			key: category,
			value: true,
		});
		isOpen.value = false;
	} else { // case: is multiselect
		if(props.values[category][index] && !(props.options[category][index].hasOwnProperty('type') && props.options[category][index].type === '*')) {
			// if the selected option is already selected and not the catch-all, deselect it
			emit('filterChanged', {
				source: props.source,
				text: props.text,
				index,
				key: category,
				value: false,
			});
			if(!Object.keys(props.values).some((key) => props.values[key].includes(true))) {
				// if after deselection all options are unselected, find catch-all and select it if it exists
				emit('filterChanged', {
					source: props.source,
					text: props.text,
					key: '0',
					index: 0,
					value: true,
				});
			}
		} else if(props.options[category][index].hasOwnProperty('type') && props.options[category][index].type === '*') {
			// if option is catch-all
			if(!props.values[category][index]) {
				// if catch-all is not already selected, deselect all options and select catch-all
				Object.keys(props.values).forEach((key) => {
					props.values[key].forEach((value, idx) => {
						if(value) {
							emit('filterChanged', {
								source: props.source,
								text: props.text,
								key,
								index: idx,
								value: false,
							});
						}
					});
				});
				emit('filterChanged', {
					source: props.source,
					text: props.text,
					index,
					key: category,
					value: true,
				});
			}
		} else {
			// else
			emit('filterChanged', {
				source: props.source,
				text: props.text,
				index,
				key: category,
				value: true,
			});
			emit('filterChanged', {
				source: props.source,
				text: props.text,
				key: '0',
				index: 0,
				value: false,
			});
		}
	}
	emit('filtersFinishedChanging');
}

function onOptionSelect(category, index) {
	choose(category, index);
	if(!props.multiselect) {
		button.value.focus();
	}
}

function onArrowUp() {
	if(selectedCategory.value === null || selectedCategoryIndex.value === null || selectedIndex.value === null) {
		selectedCategory.value = '0';
		selectedCategoryIndex.value = 0;
		selectedIndex.value = 0;
        setActiveDescendant();
        if (isMounted.value) {
		    listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
        }
		return;
	}
	if(selectedIndex.value === 0) {
		// get prev category, last option
		const categoriesArr = Object.keys(props.options);
		if(categoriesArr.indexOf(selectedCategory.value) === 0) {
			// if it's the first category
			selectedCategory.value = categoriesArr[categoriesArr.length - 1];
			selectedCategoryIndex.value = categoriesArr.length - 1;
			selectedIndex.value = props.options[selectedCategory.value].length - 1;
            setActiveDescendant();
            if (isMounted.value) {
			    listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
            }
		} else {
			selectedCategory.value = categoriesArr[categoriesArr.indexOf(selectedCategory.value) - 1];
			selectedCategoryIndex.value = categoriesArr.indexOf(selectedCategory.value);
			selectedIndex.value = props.options[selectedCategory.value].length - 1;
            setActiveDescendant();
            if (isMounted.value) {
			    listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
                listbox.value.scrollTop -= 28;
            }
		}
	} else {
		selectedIndex.value--;
        setActiveDescendant();
        if (isMounted.value) {
            listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
            listbox.value.scrollTop -= 36;
        }
	}
}

function onArrowDown() {
	if(selectedCategory.value === null || selectedCategoryIndex.value === null || selectedIndex.value === null) {
		selectedCategory.value = '0';
		selectedCategoryIndex.value = 0;
		selectedIndex.value = 0;
        setActiveDescendant();
        if (isMounted.value) {
		    listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
        }
		return;
	}
	if(selectedIndex.value === props.options[selectedCategory.value].length - 1) {
		// get next category, first option
		const categoriesArr = Object.keys(props.options);
		if(categoriesArr.indexOf(selectedCategory.value) === categoriesArr.length - 1) {
			// if it's the last category
			selectedCategory.value = categoriesArr[0];
			selectedCategoryIndex.value = 0;
			selectedIndex.value = 0;
            setActiveDescendant();
            if (isMounted.value) {
                listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
                listbox.value.scrollTop -= 28;
            }
		} else {
			selectedCategory.value = categoriesArr[categoriesArr.indexOf(selectedCategory.value) + 1];
			selectedCategoryIndex.value = categoriesArr.indexOf(selectedCategory.value);
			selectedIndex.value = 0;
            setActiveDescendant();
            if (isMounted.value) {
			    listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
            }
		}
	} else {
		selectedIndex.value++;
        setActiveDescendant();
        if (isMounted.value) {
            try {
                listbox.value.children[selectedCategoryIndex.value].children[1].children[selectedIndex.value].scrollIntoView({ block: 'nearest' });
            } catch (error) {
                Bugsnag.notify(error, (event) => {
                    event.addMetadata('DropdownByGroup:onArrowDown', {
                        selectedCategory: selectedCategory.value,
                        selectedIndex: selectedIndex.value,
                        selectedCategoryIndex: selectedCategoryIndex.value,
                        text: props.text,
                        source: props.source,
                        catchAllCategory: props.catchAllCategory,
                        listbox: listbox.value,
                        listboxChildren: listbox.value.children,
                        options: props.options,
                        values: props.values,
                        multiselect: props.multiselect,
                        isMounted: isMounted.value,
                        isOpen: isOpen.value,
                        activeDescendant: activeDescendant.value,
                        button: button.value,
                    });
                });
                console.log(error);
            }
        }
	}
}

function onMouseMoveOption(index) {
	selectedIndex.value = index;
    setActiveDescendant();
}

function onMouseMoveCategory(category, categoryIndex) {
	selectedCategory.value = category;
	selectedCategoryIndex.value = categoryIndex;
    setActiveDescendant();
}


function onEscape() {
	isOpen.value = false;
	button.value.focus();
}
</script>
